import React from 'react';
import { initialsFor } from '../data/team';
import type { TeamMember } from '../data/team';
import type { TapeColor } from '../types';
import '../styles/TeamMemberCard.css';

interface TeamMemberCardProps {
  member: TeamMember;
  tapeColor?: TapeColor;
  rotation?: number;
}

const TAPE_VALUES: Record<TapeColor, string> = {
  ochre: 'rgba(198,140,59,.55)',
  terracotta: 'rgba(194,90,58,.55)',
  sage: 'rgba(122,139,95,.55)',
};

interface CardStyle extends React.CSSProperties {
  '--rot'?: string;
  '--tape-bg'?: string;
}

/**
 * Polaroid-style card for a governance member. No photo source is
 * available from the old site that fits the aesthetic, so we use a
 * monogram set in the Italiana display face on the navy frame.
 * Reads as a portrait at a glance.
 */
const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
  member,
  tapeColor = 'ochre',
  rotation,
}) => {
  const r = rotation ?? rotationForId(member.id);
  const style: CardStyle = {
    '--rot': `${r}deg`,
    '--tape-bg': TAPE_VALUES[tapeColor],
  };

  return (
    <figure className="team-card" style={style}>
      <div className="team-card__tape" aria-hidden="true" />
      <div className="team-card__frame">
        <span className="team-card__mono" aria-hidden="true">
          {initialsFor(member.name)}
        </span>
      </div>
      <figcaption className="team-card__caption">
        <span className="team-card__name">{member.name}</span>
        <span className="team-card__role">{member.role}</span>
      </figcaption>
    </figure>
  );
};

/** Deterministic rotation in roughly ±3°, based on id hash. */
function rotationForId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  const r = ((h % 600) - 300) / 100; // -3.00 .. +3.00
  return Number(r.toFixed(2));
}

export default TeamMemberCard;
